import {
  Order,
  OrderItem,
  Message,
  Conversation,
  Product,
  ApiResponse,
  PaginatedResponse,
  UpdateOrderStatusResponse,
  PdfProcessResponse,
  PdfProcessErrorResponse,
  KameSyncResult,
  KameoneFicha,
  ApiKey,
  SyncProductsResponse,
  UnifiedThread,
  EmailIntakeDetail,
} from "../types/api";

// Call backend API directly (CORS must be configured on backend)
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Helper function to create fetch options with authentication
const createFetchOptions = (
  method: string = "GET",
  body?: any,
  token?: string | null
): RequestInit => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // Add Authorization header if token is provided
  if (token && token.trim() !== "") {
    headers["Authorization"] = `Bearer ${token}`;
  } else {
  }

  const options: RequestInit = {
    method,
    headers,
  };

  if (body && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    options.body = JSON.stringify(body);
  }

  return options;
};

// Helper function to normalize phone numbers (remove whatsapp: prefix)
const normalizePhone = (phone: string): string => {
  // Remove whatsapp: prefix
  let cleaned = phone.replace(/^whatsapp:/, "");
  // Remove + prefix
  cleaned = cleaned.replace(/^\+/, "");
  // Remove spaces and dashes
  cleaned = cleaned.replace(/[\s-]/g, "");

  // If phone starts with 9 and has 9 digits, add 56 prefix (Chile format)
  // Example: 965250692 -> 56965250692
  if (/^9\d{8}$/.test(cleaned)) {
    cleaned = "56" + cleaned;
  }

  return cleaned;
};

// Helper function to map backend status to frontend status
const mapBackendStatusToFrontend = (status: string): string => {
  if (!status) return "pendiente";

  const statusLower = status.toLowerCase();

  // If status is already in frontend format, return it as is
  const frontendStatuses = [
    "pendiente",
    "facturaGenerada",
    "facturaEnviada",
    "descartado",
  ];
  if (frontendStatuses.includes(statusLower)) {
    return statusLower;
  }

  // Map backend status to frontend status
  const statusMap: Record<string, string> = {
    pending: "pendiente",
    pre_invoiced: "facturaGenerada",
    invoiced: "facturaEnviada",
    discarded: "descartado",
    // Backward compatibility
    confirmed: "facturaEnviada",
    processing: "facturaEnviada",
    shipped: "facturaEnviada",
    rejected: "descartado",
    delivered: "facturaEnviada",
    cancelled: "descartado",
    procesado: "facturaEnviada",
    "no-procesado": "descartado",
  };

  return statusMap[statusLower] || "pendiente";
};

// Reads the response body on error and throws a meaningful message.
// For 429 it surfaces the backend "message" so the rate-limit UI can parse it.
const throwForStatus = async (response: Response): Promise<never> => {
  const data = await response.json().catch(() => ({}));
  const msg = data.message || data.error || `HTTP error! status: ${response.status}`;
  throw new Error(msg);
};

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  throw new Error(error.message || "Failed to fetch data from API");
};

// Get all orders
export const getOrders = async (
  limit: number = 50,
  offset: number = 0,
  token?: string | null
): Promise<Order[]> => {
  try {
    const url = `${API_BASE_URL}/api/v1/orders?limit=${limit}&offset=${offset}`;

    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(url, options);
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: PaginatedResponse<Order> = await response.json();
    
    if (!data || !data.success) {
      throw new Error("Invalid response from API");
    }
    
    const orders = data.data || [];
    return orders;
  } catch (error) {
    throw error; // Don't use handleApiError, let the error bubble up
  }
};

// Get orders filtered by date (for Kame column)
export const getOrdersByDate = async (
  date: string,
  status?: string,
  token?: string | null
): Promise<Order[]> => {
  try {
    let url = `${API_BASE_URL}/api/v1/orders/by-date?date=${date}`;
    if (status) {
      url += `&status=${status}`;
    }

    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.success) {
      throw new Error("Invalid response from API");
    }

    return data.data || [];
  } catch (error) {
    throw error;
  }
};

// Get order by ID
export const getOrderById = async (
  id: number,
  token?: string | null
): Promise<Order> => {
  try {
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/${id}`,
      options
    );
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Order> = await response.json();

    // Debug logging
    
    if (!data.success || !data.data) {
      throw new Error("Order not found");
    }
    
    // Map backend status to frontend status
    const order = data.data;
    order.status = mapBackendStatusToFrontend(order.status);
    
    return order;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get orders by customer phone
export const getOrdersByCustomerPhone = async (
  phone: string,
  limit: number = 10,
  offset: number = 0,
  token?: string | null
): Promise<Order[]> => {
  try {
    const normalizedPhone = normalizePhone(phone);
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/customer/${normalizedPhone}?limit=${limit}&offset=${offset}`,
      options
    );
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Order[]> = await response.json();
    return data.data || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Update order status
export const updateOrderStatus = async (
  id: number,
  status: string,
  token?: string | null,
  extra?: { documentType?: string; documentDate?: string; paymentType?: string; paymentDate?: string; tipoDocumento?: 'VENTA' | 'PEDIDO' }
): Promise<UpdateOrderStatusResponse> => {
  try {
    const options = createFetchOptions("PUT", { status, ...extra }, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/${id}/status`,
      options
    );
    
    const data: ApiResponse<Order> & { kameSync?: KameSyncResult } = await response.json();

    // Si no hay data, lanzar error
    if (!data.data) {
      throw new Error(data.error || data.message || "Failed to update order status");
    }

    const result = {
      order: data.data,
      kameSync: data.kameSync
    };
    
    // Si el endpoint retornó success: false (especialmente con HTTP 400),
    // aún retornamos el resultado para que el frontend pueda manejar el error
    // El resultado incluye order (que puede seguir en "pending") y kameSync con error
    if (!data.success && response.status >= 400) {
    }
    
    return result;
  } catch (error) {
    // Si hay error de red o parsing, lanzar error
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(errorMessage);
  }
};

// Get all conversations
export const getAllConversations = async (
  token?: string | null
): Promise<Conversation[]> => {
  try {
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/conversations`,
      options
    );
    
    if (!response.ok) {
      // Handle 403 Forbidden (admin access required) gracefully
      if (response.status === 403) {
        return [];
      }
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Conversation[]> = await response.json();
    return data.data || [];
  } catch (error) {
    // If it's a 403 error, return empty array instead of throwing
    if (error instanceof Error && error.message.includes("403")) {
      return [];
    }
    // For other errors, still throw
    return handleApiError(error);
  }
};

// Get conversation by phone number
export const getConversationByPhone = async (
  phone: string,
  token?: string | null
): Promise<Conversation | null> => {
  try {
    // Remove + prefix if present
    const cleanPhone = phone.replace(/^\+/, "");
    const normalizedPhone = normalizePhone(cleanPhone);

    const conversations = await getAllConversations(token);

    const conversation = conversations.find((conv) => {
      const convPhone = normalizePhone(conv.customerPhone);
      const matches = convPhone === normalizedPhone && conv.status === "active";
      if (matches) {
      }
      return matches;
    });

    if (!conversation) {
    }
    
    return conversation || null;
  } catch (error) {
    return null;
  }
};

// Get conversation messages
export const getConversationMessages = async (
  conversationId: number,
  limit: number = 50,
  token?: string | null
): Promise<Message[]> => {
  try {
    const url = `${API_BASE_URL}/api/v1/admin/conversations/${conversationId}/messages?limit=${limit}`;

    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(url, options);

    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Message[]> = await response.json();

    return data.data || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Send manual message
export const sendManualMessage = async (
  conversationId: number,
  message: string,
  token?: string | null
): Promise<Message> => {
  try {
    const options = createFetchOptions(
      "POST",
      {
        message: message,
        aiModeEnabled: false,
      },
      token
    );
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/conversations/${conversationId}/messages`,
      options
    );
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Message> = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error("Failed to send message");
    }
    
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Set manual mode for a conversation
export const setManualMode = async (
  conversationId: number,
  enabled: boolean,
  token?: string | null
): Promise<Conversation> => {
  try {
    const options = createFetchOptions(
      "PUT",
      {
        manualMode: enabled,
      },
      token
    );
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/conversations/${conversationId}/manual-mode`,
      options
    );
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Conversation> = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error("Failed to set manual mode");
    }
    
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product by ID (for order items)
export const getProductById = async (
  id: number,
  token?: string | null
): Promise<Product> => {
  try {
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/products/${id}`,
      options
    );
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<Product> = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error("Product not found");
    }
    
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get products with search (for editing order items)
export const getProducts = async (
  search?: string,
  limit: number = 50,
  page: number = 1,
  token?: string | null
): Promise<PaginatedResponse<Product>> => {
  try {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('q', search);
    queryParams.append('limit', limit.toString());
    queryParams.append('page', page.toString());
    
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/products?${queryParams.toString()}`,
      options
    );
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: PaginatedResponse<Product> = await response.json();
    
    if (!data.success) {
      throw new Error("Failed to fetch products");
    }
    
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateProduct = async (
  id: number,
  updates: { name?: string; price?: number; category?: string | null },
  token?: string | null
): Promise<Product> => {
  const options = createFetchOptions("PUT", updates, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/products/${id}`, options);
  if (!response.ok) return throwForStatus(response);
  const data = await response.json();
  return data.data;
};

export const deleteProduct = async (
  id: number,
  token?: string | null
): Promise<void> => {
  const options = createFetchOptions("DELETE", undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/products/${id}`, options);
  if (!response.ok) return throwForStatus(response);
};

// Update order item (product and/or quantity)
export const updateOrderItem = async (
  orderId: number,
  itemId: number,
  updates: {
    productId?: number;
    quantity?: number;
    quantityUnit?: string;
  },
  token?: string | null
): Promise<OrderItem & { product: Product }> => {
  try {
    const options = createFetchOptions("PUT", updates, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/${orderId}/items/${itemId}`,
      options
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    const data: ApiResponse<OrderItem & { product: Product }> = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error("Failed to update order item");
    }
    
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete order item
export const deleteOrderItem = async (
  orderId: number,
  itemId: number,
  token?: string | null
): Promise<void> => {
  try {
    const options = createFetchOptions("DELETE", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/${orderId}/items/${itemId}`,
      options
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    const data: ApiResponse<{ orderId: number; total: number; itemsCount: number }> = await response.json();
    if (!data.success) {
      throw new Error("Failed to delete order item");
    }
  } catch (error) {
    return handleApiError(error);
  }
};

// Add order item
export const addOrderItem = async (
  orderId: number,
  item: { productId: number; quantity: number; quantityUnit?: string },
  token?: string | null
): Promise<OrderItem & { product: Product }> => {
  try {
    const options = createFetchOptions("POST", item, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/${orderId}/items`,
      options
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    const data: ApiResponse<OrderItem & { product: Product }> = await response.json();
    if (!data.success || !data.data) {
      throw new Error("Failed to add order item");
    }
    
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create new order
export const createOrder = async (
  orderData: {
    items: Array<{
      productId: number;
      quantity: number;
      unitPrice?: number;
    }>;
    customerRut?: string; // RUT del cliente de KAME (opcional)
    customerName?: string; // Nombre del cliente de KAME (opcional)
    customerPhone?: string; // Teléfono del cliente de KAME (opcional)
    customerEmail?: string; // Email del cliente de KAME (opcional)
    customerGiro?: string; // Giro del cliente de KAME (opcional)
    deliveryAddress?: string;
    deliveryComuna?: string;
    deliveryCity?: string;
    deliveryAddressName?: string;
    deliveryRecipientName?: string;
    pdfFile?: File;
    kameoneFolio?: string;
    orderNumber?: string | null;
    orderDate?: string | null;
    priceListName?: string;
    poNumber?: string;
    poDate?: string;
    documentDate?: string;
    // From processPdfOrder/processDocumentOrder. Sent back so the backend can
    // finalize the extraction audit row. Omitted for manual orders.
    correlationId?: string;
  },
  token?: string | null
): Promise<Order> => {
  try {
    // Si hay PDF, usar FormData
    if (orderData.pdfFile) {
      // Validar tamaño (10MB)
      if (orderData.pdfFile.size > 10 * 1024 * 1024) {
        throw new Error('El PDF no puede ser mayor a 10MB');
      }
      // Validar tipo
      if (orderData.pdfFile.type !== 'application/pdf') {
        throw new Error('Solo se permiten archivos PDF');
      }

      const formData = new FormData();
      formData.append('pdf', orderData.pdfFile);
      formData.append('items', JSON.stringify(orderData.items));
      
      // Agregar todos los campos del cliente si existen
      if (orderData.customerRut) {
        formData.append('customerRut', orderData.customerRut);
      }
      if (orderData.customerName) {
        formData.append('customerName', orderData.customerName);
      }
      if (orderData.customerPhone) {
        formData.append('customerPhone', orderData.customerPhone);
      }
      if (orderData.customerEmail) {
        formData.append('customerEmail', orderData.customerEmail);
      }
      if (orderData.customerGiro) {
        formData.append('customerGiro', orderData.customerGiro);
      }
      if (orderData.deliveryAddress) {
        formData.append('deliveryAddress', orderData.deliveryAddress);
      }
      if (orderData.deliveryComuna) {
        formData.append('deliveryComuna', orderData.deliveryComuna);
      }
      if (orderData.deliveryCity) {
        formData.append('deliveryCity', orderData.deliveryCity);
      }
      if (orderData.deliveryAddressName) {
        formData.append('deliveryAddressName', orderData.deliveryAddressName);
      }
      if (orderData.deliveryRecipientName) {
        formData.append('deliveryRecipientName', orderData.deliveryRecipientName);
      }
      if (orderData.kameoneFolio) {
        formData.append('kameoneFolio', orderData.kameoneFolio);
      }
      if (orderData.orderNumber) {
        formData.append('orderNumber', orderData.orderNumber);
      }
      if (orderData.orderDate) {
        formData.append('orderDate', orderData.orderDate);
      }
      if (orderData.priceListName) {
        formData.append('priceListName', orderData.priceListName);
      }
      if (orderData.poNumber) {
        formData.append('poNumber', orderData.poNumber);
      }
      if (orderData.poDate) {
        formData.append('poDate', orderData.poDate);
      }
      if (orderData.correlationId) {
        formData.append('correlationId', orderData.correlationId);
      }

      const headers: Record<string, string> = {
        Accept: "application/json",
      };

      if (token && token.trim() !== "") {
        headers["Authorization"] = `Bearer ${token}`;
      }


      // Log FormData completo antes de enviar

      // 🔍 DEBUG: Datos antes de enviar al backend (FormData)

      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders`,
        {
          method: "POST",
          headers,
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        let errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
        
        if (errorData.details && Array.isArray(errorData.details)) {
          errorMessage = `${errorMessage}: ${errorData.details.join(', ')}`;
        }
        
        if (errorData.message) {
          errorMessage = errorData.message;
          if (errorData.details && Array.isArray(errorData.details)) {
            errorMessage = `${errorMessage}: ${errorData.details.join(', ')}`;
          }
        }
        
        throw new Error(errorMessage);
      }
      
      const data: ApiResponse<Order> = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error("Failed to create order");
      }
      
      const order = data.data;
      order.status = mapBackendStatusToFrontend(order.status);
      
      return order;
    } else {
      // Sin PDF, usar JSON
      const body: any = {
        items: orderData.items,
      };
      
      // Agregar todos los campos del cliente si existen
      if (orderData.customerRut) {
        body.customerRut = orderData.customerRut;
      }
      if (orderData.customerName) {
        body.customerName = orderData.customerName;
      }
      if (orderData.customerPhone) {
        body.customerPhone = orderData.customerPhone;
      }
      if (orderData.customerEmail) {
        body.customerEmail = orderData.customerEmail;
      }
      if (orderData.customerGiro) {
        body.customerGiro = orderData.customerGiro;
      }
      if (orderData.deliveryAddress) {
        body.deliveryAddress = orderData.deliveryAddress;
      }
      if (orderData.deliveryComuna) {
        body.deliveryComuna = orderData.deliveryComuna;
      }
      if (orderData.deliveryCity) {
        body.deliveryCity = orderData.deliveryCity;
      }
      if (orderData.deliveryAddressName) {
        body.deliveryAddressName = orderData.deliveryAddressName;
      }
      if (orderData.deliveryRecipientName) {
        body.deliveryRecipientName = orderData.deliveryRecipientName;
      }
      if (orderData.kameoneFolio) {
        body.kameoneFolio = orderData.kameoneFolio;
      }
      if (orderData.orderNumber) {
        body.orderNumber = orderData.orderNumber;
      }
      if (orderData.orderDate) {
        body.orderDate = orderData.orderDate;
      }
      if (orderData.priceListName) {
        body.priceListName = orderData.priceListName;
      }
      if (orderData.poNumber) {
        body.poNumber = orderData.poNumber;
      }
      if (orderData.poDate) {
        body.poDate = orderData.poDate;
      }
      if (orderData.documentDate) {
        body.documentDate = orderData.documentDate;
      }
      if (orderData.correlationId) {
        body.correlationId = orderData.correlationId;
      }

      // 🔍 DEBUG: Datos antes de enviar al backend (JSON)
      
      const options = createFetchOptions("POST", body, token);
      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders`,
        options
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        let errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
        
        if (errorData.details && Array.isArray(errorData.details)) {
          errorMessage = `${errorMessage}: ${errorData.details.join(', ')}`;
        }
        
        if (errorData.message) {
          errorMessage = errorData.message;
          if (errorData.details && Array.isArray(errorData.details)) {
            errorMessage = `${errorMessage}: ${errorData.details.join(', ')}`;
          }
        }
        
        throw new Error(errorMessage);
      }
      
      const data: ApiResponse<Order> = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error("Failed to create order");
      }
      
      const order = data.data;
      order.status = mapBackendStatusToFrontend(order.status);
      
      return order;
    }
  } catch (error) {
    return handleApiError(error);
  }
};

// Helper function to create multipart/form-data fetch options for PDF upload
const createMultipartFetchOptions = (
  file: File,
  token?: string | null
): { headers: Record<string, string>; body: FormData } => {
  const formData = new FormData();
  formData.append("pdf", file);

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  // Add Authorization header if token is provided
  // NOTE: Don't set Content-Type header - browser will set it automatically with boundary
  if (token && token.trim() !== "") {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return {
    headers,
    body: formData,
  };
};

// Process PDF and generate pre-filled order
// Retries a fetch once on network-level failure (e.g. dyno cold start / CORS miss).
// Only retries on TypeError (no response), never on HTTP error responses.
async function fetchWithColdStartRetry(
  input: RequestInfo,
  init: RequestInit,
  retryDelayMs = 5000
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    if (err instanceof TypeError) {
      await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      return fetch(input, init);
    }
    throw err;
  }
}

export const processPdfOrder = async (
  pdfFile: File,
  token?: string | null
): Promise<PdfProcessResponse> => {
  try {
    // Validación frontend (tipo PDF y tamaño máximo 10MB)
    if (pdfFile.type !== 'application/pdf') {
      throw new Error('Solo se permiten archivos PDF');
    }
    
    if (pdfFile.size > 10 * 1024 * 1024) {
      throw new Error('El archivo no puede exceder 10MB');
    }

    const { headers, body } = createMultipartFetchOptions(pdfFile, token);
    
    // Agregar timeout a la petición (60 segundos)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    
    let response: Response;
    try {
      response = await fetchWithColdStartRetry(
        `${API_BASE_URL}/api/v1/orders/from-pdf`,
        { method: "POST", headers, body, signal: controller.signal }
      );
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        throw new Error('La petición tardó demasiado tiempo. Por favor, intenta con un archivo más pequeño.');
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorData: PdfProcessErrorResponse = await response.json().catch(() => ({
        success: false as const,
        error: `HTTP error! status: ${response.status}`,
        timestamp: new Date().toISOString(),
      }));
      
      // Manejar ambos formatos de error (con y sin message)
      throw new Error(
        errorData.error || errorData.message || 'Error processing PDF'
      );
    }

    const data: PdfProcessResponse = await response.json();

    if (!data.success || !data.data) {
      throw new Error("Failed to process PDF");
    }

    return data;
  } catch (error) {
    // Re-throw si ya es un Error con mensaje
    if (error instanceof Error) {
      throw error;
    }
    // Si no, usar handleApiError
    return handleApiError(error);
  }
};

// Process any supported document (PDF, XLSX, XLS, CSV) → pre-filled order data
export const processDocumentOrder = async (
  file: File,
  token?: string | null
): Promise<PdfProcessResponse> => {
  try {
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('El archivo no puede exceder 10MB');
    }

    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token && token.trim() !== '') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    let response: Response;
    try {
      response = await fetchWithColdStartRetry(
        `${API_BASE_URL}/api/v1/orders/from-document`,
        { method: 'POST', headers, body: formData, signal: controller.signal }
      );
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        throw new Error('La petición tardó demasiado tiempo. Por favor, intenta con un archivo más pequeño.');
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorData: PdfProcessErrorResponse = await response.json().catch(() => ({
        success: false as const,
        error: `HTTP error! status: ${response.status}`,
        timestamp: new Date().toISOString(),
      }));
      throw new Error(errorData.error || errorData.message || 'Error procesando el archivo');
    }

    const data: PdfProcessResponse = await response.json();
    if (!data.success || !data.data) {
      throw new Error('Error procesando el archivo');
    }

    return data;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error('Error inesperado al procesar el archivo');
  }
};

// Get Kameone Fichas (Clientes)
export const getKameoneFichas = async (
  rut?: string,
  razonSocial?: string,
  token?: string | null
): Promise<KameoneFicha[]> => {
  try {
    const params = new URLSearchParams();
    if (rut) params.append('rut', rut);
    if (razonSocial) params.append('razonSocial', razonSocial);
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/v1/kameone/fichas${queryString ? `?${queryString}` : ''}`;
    
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(url, options);
    
    if (!response.ok) {
      return throwForStatus(response);
    }
    
    const data: ApiResponse<KameoneFicha[]> = await response.json();
    
    if (!data.success || !data.data) {
      return [];
    }
    
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get ALL Kameone Fichas — no filter, backend caches 24h
export const getAllKameoneFichas = async (token?: string | null): Promise<KameoneFicha[]> => {
  try {
    const url = `${API_BASE_URL}/api/v1/kameone/fichas/all`;
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(url, options);
    if (!response.ok) return throwForStatus(response);
    const data: ApiResponse<KameoneFicha[]> = await response.json();
    if (!data.success || !data.data) return [];
    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update order customer
export const updateOrderCustomer = async (
  id: number,
  customerData: {
    customerRut: string;
    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;
    customerGiro?: string;
    deliveryAddress?: string;
    deliveryComuna?: string;
    deliveryCity?: string;
  },
  token?: string | null
): Promise<Order> => {
  try {
    const options = createFetchOptions("PUT", customerData, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/orders/${id}/customer`,
      options
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data: ApiResponse<Order> = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error("Failed to update order customer");
    }
    
    const order = data.data;
    order.status = mapBackendStatusToFrontend(order.status);
    
    return order;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updatePendingOrder = async (
  id: number,
  orderData: {
    items: Array<{ productId: number; quantity: number; unitPrice?: number; pdfLineText?: string }>;
    customerRut?: string;
    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;
    customerGiro?: string;
    deliveryAddress?: string;
    deliveryComuna?: string;
    deliveryCity?: string;
    kameoneFolio?: string;
    priceListName?: string;
  },
  token?: string | null
): Promise<Order> => {
  try {
    const options = createFetchOptions('PUT', orderData, token);
    const response = await fetch(`${API_BASE_URL}/api/v1/orders/${id}`, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse<Order> = await response.json();
    if (!data.success || !data.data) throw new Error('Failed to update order');

    const order = data.data;
    order.status = mapBackendStatusToFrontend(order.status);
    return order;
  } catch (error) {
    return handleApiError(error);
  }
};

// ============================================
// API KEY MANAGEMENT
// ============================================

// Get all API keys for organization
export const getApiKeys = async (
  token?: string | null
): Promise<ApiKey[]> => {
  try {
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/api-keys`,
      options
    );

    if (!response.ok) {
      return throwForStatus(response);
    }

    const data: ApiResponse<ApiKey[]> = await response.json();

    if (!data.success || !data.data) {
      return [];
    }

    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get specific API key (returns masked value)
export const getApiKey = async (
  keyName: string,
  token?: string | null
): Promise<{ keyName: string; value: string; exists: boolean }> => {
  try {
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/api-keys/${keyName}`,
      options
    );

    if (!response.ok) {
      return throwForStatus(response);
    }

    const data: ApiResponse<{ keyName: string; value: string; exists: boolean }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error("API key not found");
    }

    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get API key for editing (returns full unmasked value)
export const getApiKeyForEdit = async (
  keyName: string,
  token?: string | null
): Promise<{ keyName: string; value: string; exists: boolean }> => {
  try {
    const options = createFetchOptions("GET", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/api-keys/${keyName}/edit`,
      options
    );

    if (!response.ok) {
      return throwForStatus(response);
    }

    const data: ApiResponse<{ keyName: string; value: string; exists: boolean }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error("API key not found");
    }

    return data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Set/update API key
export const setApiKey = async (
  keyName: string,
  value: string,
  token?: string | null
): Promise<void> => {
  try {
    const options = createFetchOptions("PUT", { keyName, value }, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/api-keys`,
      options
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to save API key");
    }
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete API key
export const deleteApiKey = async (
  keyName: string,
  token?: string | null
): Promise<void> => {
  try {
    const options = createFetchOptions("DELETE", undefined, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/api-keys/${keyName}`,
      options
    );

    if (!response.ok) {
      return throwForStatus(response);
    }

    const data: ApiResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to delete API key");
    }
  } catch (error) {
    return handleApiError(error);
  }
};

// ============================================
// PRODUCT SYNC
// ============================================

// Sync products from Kameone
export const createProduct = async (
  product: { name: string; sku: string; price: number; unidadDeNegocio?: string },
  token?: string | null
): Promise<Product> => {
  const options = createFetchOptions("POST", product, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/products`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || err.message || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data ?? data;
};

export const syncProducts = async (
  confirmationToken: string,
  token?: string | null
): Promise<SyncProductsResponse> => {
  try {
    const options = createFetchOptions("POST", { confirmationToken }, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/sync-products`,
      options
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data: SyncProductsResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || data.message || "Product sync failed");
    }

    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const syncProductsFromKameone = async (token?: string | null): Promise<any> => {
  try {
    const options = createFetchOptions("POST", {}, token);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/products/sync`,
      options
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || data.message || "Product sync failed");
    }

    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// ============================================================
// VENDEDORES
// ============================================================

export interface Vendedor {
  id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export const getVendedores = async (token?: string | null): Promise<Vendedor[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/vendedores`, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as Vendedor[];
};

// ─── Extraction audits (vista /check) ──────────────────────────────────────────

export interface ExtractionAudit {
  id: number;
  orderId: number | null;
  sourceType: string | null;
  fileName: string | null;
  createdAt: string;
  riskScore: number | null;
  correctedFields: string[];
  diff: any;
}

export const getExtractionAudits = async (
  date: string,
  token?: string | null
): Promise<ExtractionAudit[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(
    `${API_BASE_URL}/api/v1/extraction-audits?date=${encodeURIComponent(date)}`,
    options
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as ExtractionAudit[];
};

export const createVendedor = async (name: string, token?: string | null): Promise<Vendedor> => {
  const options = createFetchOptions('POST', { name }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/vendedores`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as Vendedor;
};

export const updateVendedor = async (id: number, name: string, token?: string | null): Promise<Vendedor> => {
  const options = createFetchOptions('PUT', { name }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/vendedores/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as Vendedor;
};

export const deleteVendedor = async (id: number, token?: string | null): Promise<void> => {
  const options = createFetchOptions('DELETE', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/vendedores/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
};

export const syncVendedoresFromKame = async (token?: string | null): Promise<{ synced: number }> => {
  const options = createFetchOptions('POST', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/vendedores/sync`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return { synced: data.synced as number };
};

// ============================================================
// CLIENT ADDRESSES
// ============================================================

export interface ClientAddress {
  id: number;
  client_name: string;
  rut?: string | null;
  nombre?: string | null;
  contacto?: string | null;
  address: string;
  comuna?: string | null;
  city?: string | null;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateClientAddressInput {
  clientName: string;
  rut?: string;
  nombre?: string;
  contacto?: string;
  address: string;
  comuna?: string;
  city?: string;
  isDefault?: boolean;
}

export interface UpdateClientAddressInput {
  clientName?: string;
  rut?: string;
  nombre?: string;
  contacto?: string;
  address?: string;
  comuna?: string;
  city?: string;
  isDefault?: boolean;
}

export const getClientAddresses = async (token?: string | null, rut?: string): Promise<ClientAddress[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const url = rut
    ? `${API_BASE_URL}/api/v1/client-addresses?rut=${encodeURIComponent(rut)}`
    : `${API_BASE_URL}/api/v1/client-addresses`;
  const response = await fetch(url, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as ClientAddress[];
};

export const createClientAddress = async (
  input: CreateClientAddressInput,
  token?: string | null,
): Promise<ClientAddress> => {
  const options = createFetchOptions('POST', input, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/client-addresses`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as ClientAddress;
};

export const updateClientAddress = async (
  id: number,
  updates: UpdateClientAddressInput,
  token?: string | null,
): Promise<ClientAddress> => {
  const options = createFetchOptions('PUT', updates, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/client-addresses/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as ClientAddress;
};

export const importClientAddressesFromKame = async (token?: string | null): Promise<{ imported: number; skipped: number }> => {
  const options = createFetchOptions('POST', {}, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/client-addresses/import-from-kame`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return { imported: data.imported, skipped: data.skipped };
};

export const deleteClientAddress = async (id: number, token?: string | null): Promise<void> => {
  const options = createFetchOptions('DELETE', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/client-addresses/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }
};

// Price Lists

export interface PriceListSummary {
  id: number;
  name: string;
  itemCount: number;
  clientCount: number;
  created_at: string;
  updated_at: string;
}

export interface PriceListItem {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  price: number;
}

export interface PriceListClient {
  id: number;
  razon_social: string;
}

export interface PriceListDetail {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  items: PriceListItem[];
  clients: PriceListClient[];
}

export const getPriceLists = async (
  token?: string | null,
): Promise<{ lists: PriceListSummary[]; defaultPriceListId: number | null }> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists`, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return {
    lists: data.data as PriceListSummary[],
    defaultPriceListId: data.defaultPriceListId ?? null,
  };
};

export const resolvePricesForPriceList = async (
  listName: string,
  productIds: number[],
  token?: string | null,
): Promise<Record<number, number>> => {
  const options = createFetchOptions('POST', { listName, productIds }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/resolve-prices`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as Record<number, number>;
};


export const assignClientToPriceList = async (
  razonSocial: string,
  listName: string | null,
  token?: string | null,
): Promise<void> => {
  const options = createFetchOptions('PUT', { razonSocial, listName }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/assign-client`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }
};

export const getCustomerPriceListAssignment = async (
  razonSocial: string,
  token?: string | null,
): Promise<string | null> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(
    `${API_BASE_URL}/api/v1/price-lists/customer-assignment?razonSocial=${encodeURIComponent(razonSocial)}`,
    options,
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data.data?.priceListName ?? null;
};

export const importPriceListCsv = async (
  file: File,
  token?: string | null,
): Promise<{ listsUpserted: number; itemsUpserted: number; clientsUpserted: number; skippedSkuNotFound: number; notFoundSkus: string[]; parseErrors: string[] }> => {
  const formData = new FormData();
  formData.append('csv', file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/import`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  return data.data;
};

export const createPriceList = async (
  name: string,
  token?: string | null,
): Promise<{ id: number; name: string }> => {
  const options = createFetchOptions('POST', { name }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as { id: number; name: string };
};

export const getPriceListById = async (
  id: number,
  token?: string | null,
): Promise<PriceListDetail> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as PriceListDetail;
};

export const updatePriceList = async (
  id: number,
  name: string,
  token?: string | null,
): Promise<{ id: number; name: string }> => {
  const options = createFetchOptions('PUT', { name }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as { id: number; name: string };
};

export const deletePriceList = async (
  id: number,
  token?: string | null,
): Promise<void> => {
  const options = createFetchOptions('DELETE', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${id}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
};

export const getPriceListItems = async (
  listId: number,
  limit: number = 50,
  offset: number = 0,
  search?: string,
  token?: string | null,
): Promise<{ items: PriceListItem[]; pagination: { limit: number; offset: number; total: number } }> => {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  if (search) params.append('search', search);
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/items?${params.toString()}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return { items: data.data as PriceListItem[], pagination: data.pagination };
};

export const addPriceListItems = async (
  listId: number,
  items: { productId: number; price: number }[],
  token?: string | null,
): Promise<{ upserted: number }> => {
  const options = createFetchOptions('POST', { items }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/items`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as { upserted: number };
};

export const updatePriceListItem = async (
  listId: number,
  itemId: number,
  price: number,
  token?: string | null,
): Promise<void> => {
  const options = createFetchOptions('PUT', { price }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/items/${itemId}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
};

export const deletePriceListItem = async (
  listId: number,
  itemId: number,
  token?: string | null,
): Promise<void> => {
  const options = createFetchOptions('DELETE', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/items/${itemId}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
};

export const getPriceListClients = async (
  listId: number,
  token?: string | null,
): Promise<PriceListClient[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/clients`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.data as PriceListClient[];
};

export const deletePriceListClient = async (
  listId: number,
  clientId: number,
  token?: string | null,
): Promise<void> => {
  const options = createFetchOptions('DELETE', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/clients/${clientId}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
};

export const exportPriceListCsv = async (
  listId: number,
  token?: string | null,
): Promise<Blob> => {
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/${listId}/export`, { headers });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
};

export const setDefaultPriceList = async (
  priceListId: number | null,
  token?: string | null,
): Promise<void> => {
  const options = createFetchOptions('PUT', { priceListId }, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/price-lists/default`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
};

// Get the next folio that will be used for the next order (preview, does not advance sequence)
export const getNextFolio = async (
  documentType?: string,
  mode: 'last_created' | 'last_kame' = 'last_kame',
  token?: string | null,
): Promise<number> => {
  const searchParams = new URLSearchParams();
  if (documentType) searchParams.set('documentType', documentType);
  searchParams.set('mode', mode);
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/orders/next-folio?${searchParams.toString()}`, options);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = await response.json();
  return (data.data as { nextFolio: number }).nextFolio;
};

export interface OrderSummaryRow {
  id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  unidad_de_negocio: string;
  customer_name: string | null;
  customer_rut: string | null;
  quantity: number;
  unit_price: number;
  order_date: string;
  order_time: string;
  delivery_address: string | null;
  delivery_comuna: string | null;
  delivery_city: string | null;
  delivery_address_name: string | null;
}

export const getDailySummary = async (
  date: string,
  token?: string | null,
  statuses?: string[]
): Promise<OrderSummaryRow[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const params = new URLSearchParams({ date });
  if (statuses && statuses.length > 0) params.set('statuses', statuses.join(','));
  const response = await fetch(`${API_BASE_URL}/api/v1/orders/summary?${params}`, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as OrderSummaryRow[];
};

export const getSummaryByRange = async (
  dateFrom: string,
  dateTo: string,
  token?: string | null,
  statuses?: string[]
): Promise<OrderSummaryRow[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const params = new URLSearchParams({ dateFrom, dateTo });
  if (statuses && statuses.length > 0) params.set('statuses', statuses.join(','));
  const response = await fetch(`${API_BASE_URL}/api/v1/orders/summary?${params}`, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as OrderSummaryRow[];
};

// Unified threads (WhatsApp + email intakes)
export const getThreads = async (token?: string | null): Promise<UnifiedThread[]> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/threads`, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as UnifiedThread[];
};

// Email intake detail (intake metadata + attachments + linked order)
export const getEmailIntakeDetail = async (
  emailIntakeId: number,
  token?: string | null
): Promise<EmailIntakeDetail> => {
  const options = createFetchOptions('GET', undefined, token);
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/threads/email/${emailIntakeId}`, options);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.data as EmailIntakeDetail;
};

// Export helper functions
export { normalizePhone, mapBackendStatusToFrontend };
